import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, Calendar, ArrowLeft, ExternalLink, 
  Sparkles, DollarSign, FileText, CheckCircle2 
} from 'lucide-react';
import { crmAPI } from '../api';

interface MunicipalityIntelligenceProps {
  municipalityId: string;
  onBack: () => void;
}

export const MunicipalityIntelligence: React.FC<MunicipalityIntelligenceProps> = ({
  municipalityId,
  onBack,
}) => {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'info' | 'gestores' | 'entidades' | 'timeline' | 'destinacoes'>('info');

  useEffect(() => {
    setLoading(true);
    crmAPI.getMunicipalityIntelligence(municipalityId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [municipalityId]);

  if (loading) {
    return (
      <div className="py-20 text-center text-slate-400 italic">
        Analisando histórico e carregando inteligência institucional do município...
      </div>
    );
  }

  if (!data || !data.municipality) {
    return (
      <div className="p-8 text-center bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800">
        <p className="text-red-500 font-semibold mb-4">Falha ao obter inteligência do município.</p>
        <button onClick={onBack} className="text-blue-500 flex items-center gap-2 mx-auto hover:underline font-bold text-xs">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
      </div>
    );
  }

  const { municipality, summary, stats, timeline } = data;

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800">
        <div>
          <button onClick={onBack} className="text-slate-400 hover:text-slate-205 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider font-mono mb-2">
            <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao CRM
          </button>
          <h1 className="text-2xl font-black text-slate-850 dark:text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-500" /> {municipality.name} - {municipality.state}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Visualização consolidada de relacionamento, emendas e demandas institucionais.
          </p>
        </div>
      </div>

      {/* Grid Indicadores Rápidos */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 rounded-2xl relative overflow-hidden flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0">
            <DollarSign className="w-6 h-6" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-slate-400 block uppercase font-mono">Total Destinado</span>
            <span className="text-lg font-black text-slate-800 dark:text-white font-mono">
              {stats.totalAllocated.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
            </span>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 rounded-2xl relative overflow-hidden flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 shrink-0">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-slate-400 block uppercase font-mono">Demandas Históricas</span>
            <span className="text-lg font-black text-slate-800 dark:text-white font-mono">
              {stats.totalDemands}
            </span>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 rounded-2xl relative overflow-hidden flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500 shrink-0">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-slate-400 block uppercase font-mono">Entidades Atendidas</span>
            <span className="text-lg font-black text-slate-800 dark:text-white font-mono">
              {stats.entitiesCount}
            </span>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 rounded-2xl relative overflow-hidden flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-500 shrink-0">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-slate-400 block uppercase font-mono">Gestores Políticos</span>
            <span className="text-lg font-black text-slate-800 dark:text-white font-mono">
              {stats.peopleCount}
            </span>
          </div>
        </div>

      </div>

      {/* Memória Institucional / Resumo IA Factual */}
      <div className="bg-slate-950 p-6 rounded-3xl border border-blue-500/20 shadow-lg relative overflow-hidden">
        <div className="absolute right-0 top-0 w-32 h-32 bg-blue-500/10 rounded-full filter blur-xl animate-pulse"></div>
        <h3 className="text-xs font-bold text-blue-400 flex items-center gap-1.5 font-mono uppercase tracking-wider mb-2">
          <Sparkles className="w-4 h-4" /> Memória Institucional Consolidada
        </h3>
        <p className="text-sm text-slate-200 leading-relaxed font-sans font-medium">
          {summary || 'Nenhum histórico ativo cadastrado no sistema para este município.'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-250 dark:border-slate-800 gap-1 overflow-x-auto pb-0.5">
        {[
          { id: 'info', label: 'Dados Gerais', icon: <Building2 className="w-3.5 h-3.5" /> },
          { id: 'gestores', label: `Gestores (${municipality.people.length})`, icon: <Users className="w-3.5 h-3.5" /> },
          { id: 'entidades', label: `Entidades (${municipality.entities.length})`, icon: <Building2 className="w-3.5 h-3.5" /> },
          { id: 'destinacoes', label: `Destinações (${municipality.allocations.length})`, icon: <DollarSign className="w-3.5 h-3.5" /> },
          { id: 'timeline', label: 'Timeline de Relação', icon: <Calendar className="w-3.5 h-3.5" /> }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold border-b-2 transition-all font-sans shrink-0 ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-500 bg-blue-500/5'
                : 'border-transparent text-slate-500 hover:text-slate-350 hover:bg-slate-500/5'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Panels */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 text-xs">
        
        {/* 1. INFO */}
        {activeTab === 'info' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 border-b border-slate-200 dark:border-slate-800 pb-2">Informações da Prefeitura</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-slate-500 block">Endereço da Sede</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-300">{municipality.addressPref || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Código IBGE</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-300 font-mono">{municipality.ibgeCode || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Telefone</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-300">{municipality.phone || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">E-mail Oficial</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-300">{municipality.email || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Região do Estado</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-300">{municipality.region || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Website</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-300">
                    {municipality.website ? (
                      <a href={municipality.website} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">
                        Acessar Portal <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : '-'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 border-b border-slate-200 dark:border-slate-800 pb-2">Observações Técnicas</h3>
              <p className="mt-3 text-slate-500 whitespace-pre-wrap leading-relaxed">
                {municipality.notes || 'Sem observações cadastradas para este município.'}
              </p>
            </div>
          </div>
        )}

        {/* 2. GESTORES */}
        {activeTab === 'gestores' && (
          <div className="space-y-4">
            {municipality.people.length === 0 ? (
              <p className="text-slate-500 italic py-6 text-center">Nenhum gestor ou autoridade vinculada a este município.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {municipality.people.map((p: any) => (
                  <div key={p.id} className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 hover:border-slate-750 transition flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-slate-700 dark:text-slate-200 text-sm">{p.name}</h4>
                      <div className="flex gap-2 items-center mt-1">
                        <span className="px-2 py-0.5 bg-blue-500/10 text-blue-500 rounded text-[9px] font-bold uppercase tracking-wider">{p.role}</span>
                        <span className="text-slate-500 text-[10px]">{p.email || '-'}</span>
                      </div>
                    </div>
                    {p.whatsapp && (
                      <a href={`https://wa.me/${p.whatsapp.replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-bold text-[10px]">
                        WhatsApp
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 3. ENTIDADES */}
        {activeTab === 'entidades' && (
          <div className="space-y-4">
            {municipality.entities.length === 0 ? (
              <p className="text-slate-500 italic py-6 text-center">Nenhuma entidade beneficiária vinculada.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {municipality.entities.map((ent: any) => (
                  <div key={ent.id} className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 flex items-start justify-between">
                    <div>
                      <h4 className="font-bold text-slate-700 dark:text-slate-200 text-sm">{ent.name}</h4>
                      <div className="text-[10px] text-slate-500 mt-1">CNPJ: {ent.cnpj || '-'}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">Responsável: {ent.responsible?.name || 'Não cadastrado'}</div>
                      <div className="mt-2 flex gap-1.5">
                        <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-950 text-slate-400 rounded font-mono text-[9px] uppercase">{ent.type}</span>
                        {ent.activityArea && <span className="px-2 py-0.5 bg-blue-500/10 text-blue-500 rounded text-[9px] font-semibold">{ent.activityArea}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 4. DESTINAÇÕES */}
        {activeTab === 'destinacoes' && (
          <div className="overflow-x-auto">
            {municipality.allocations.length === 0 ? (
              <p className="text-slate-500 italic py-6 text-center">Nenhuma destinação registrada para este município.</p>
            ) : (
              <table className="w-full text-left">
                <thead className="bg-slate-50 dark:bg-slate-950 text-[10px] text-slate-400 uppercase font-bold border-b border-slate-200 dark:border-slate-850">
                  <tr>
                    <th className="p-3">Valor</th>
                    <th className="p-3">Objeto</th>
                    <th className="p-3">Entidade</th>
                    <th className="p-3">Ano</th>
                    <th className="p-3">Autoridade</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {municipality.allocations.map((a: any) => (
                    <tr key={a.id} className="border-b border-slate-100 dark:border-slate-850 hover:bg-slate-500/5">
                      <td className="p-3 font-bold text-slate-800 dark:text-slate-100">
                        {Number(a.value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </td>
                      <td className="p-3 font-semibold text-slate-700 dark:text-slate-350">{a.object?.name}</td>
                      <td className="p-3 text-slate-500">{a.entity?.name || 'Prefeitura Direta'}</td>
                      <td className="p-3 text-slate-500 font-mono">{a.year}</td>
                      <td className="p-3 text-slate-500">{a.authority?.name}</td>
                      <td className="p-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded font-bold text-[9px] ${
                          a.status === 'PAGA' || a.status === 'ENTREGUE'
                            ? 'bg-emerald-500/10 text-emerald-500'
                            : 'bg-amber-500/10 text-amber-500'
                        }`}>
                          {a.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* 5. TIMELINE */}
        {activeTab === 'timeline' && (
          <div className="space-y-6 relative pl-6 before:absolute before:inset-y-0 before:left-2 before:w-0.5 before:bg-slate-200 dark:before:bg-slate-800 py-2">
            {timeline.length === 0 ? (
              <p className="text-slate-500 italic text-center before:hidden pl-0">Nenhuma interação registrada com o gabinete.</p>
            ) : (
              timeline.map((event: any, index: number) => (
                <div key={index} className="relative group">
                  {/* Ponto na timeline */}
                  <span className={`absolute -left-[22px] top-1.5 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-slate-900 group-hover:scale-125 transition-transform ${
                    event.type === 'RECURSO' ? 'bg-emerald-500' : 'bg-blue-500'
                  }`} />
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1">
                    <span className="font-bold text-slate-700 dark:text-slate-200 text-xs sm:text-sm">{event.title}</span>
                    <span className="font-mono text-[10px] text-slate-400">{event.date}</span>
                  </div>
                  <p className="text-slate-500 text-xs">{event.details}</p>
                </div>
              ))
            )}
          </div>
        )}

      </div>

    </div>
  );
};
